import React, { useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { FiUploadCloud, FiDownload, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

const AddStudentForm = ({
  newStudent,
  setNewStudent,
  onSubmit,
  loading: formLoading,
  onImportSuccess,
}) => {
  const { token } = useAuth();
  const [activeMode, setActiveMode] = useState("single"); // "single" | "import"
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Download Sample CSV Template (RFC-4180 with UTF-8 BOM)
  const handleDownloadTemplate = () => {
    const headers = [
      "Faculty",
      "School",
      "Programme",
      "Specialization",
      "Reg No",
      "Student Name",
      "Email",
      "Gender",
      "Batch",
      "Credits",
      "CGPA",
    ];

    const escapeVal = (v) => `"${String(v).replace(/"/g, '""')}"`;

    const sampleRows = [
      [
        "Faculty of Engineering",
        "School of Computing and Information Technology",
        "B.Tech",
        "Computer Science & Engineering",
        "209301099",
        "Rahul Sharma",
        "rahul.sharma@jaipur.manipal.edu",
        "M",
        "2020-2024",
        "180",
        "8.50",
      ],
      [
        "Faculty of Engineering",
        "School of Computing and Information Technology",
        "B.Tech",
        "Information Technology",
        "209301100",
        "Priya Verma",
        "priya.verma@jaipur.manipal.edu",
        "F",
        "2020-2024",
        "180",
        "9.10",
      ],
    ];

    const csvContent =
      "\uFEFF" +
      [
        headers.map(escapeVal).join(","),
        ...sampleRows.map((r) => r.map(escapeVal).join(",")),
      ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "student_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle Drag & Drop
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setImportFile(e.dataTransfer.files[0]);
      setImportResult(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
      setImportResult(null);
    }
  };

  // Submit Import
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    const formData = new FormData();
    formData.append("file", importFile);

    try {
      setImportLoading(true);
      setImportResult(null);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/student/bulk-import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setImportResult({
        success: true,
        message: res.data?.message || "Import completed successfully!",
        data: res.data,
      });
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (onImportSuccess) onImportSuccess();
    } catch (err) {
      setImportResult({
        success: false,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to import file. Please check file format and columns.",
      });
    } finally {
      setImportLoading(false);
    }
  };

  const [fieldErrors, setFieldErrors] = useState({});

  // Single Form Validation
  const handleSingleFormSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    if (!newStudent.reg_no || !newStudent.reg_no.trim()) {
      errors.reg_no = "Registration Number is required";
    }
    if (!newStudent.student_name || !newStudent.student_name.trim()) {
      errors.student_name = "Full Name is required";
    }
    if (newStudent.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newStudent.email.trim())) {
      errors.email = "Please enter a valid email address";
    }
    if (!newStudent.batch || !newStudent.batch.trim()) {
      errors.batch = "Batch is required";
    }
    if (!newStudent.faculty || !newStudent.faculty.trim()) {
      errors.faculty = "Faculty is required";
    }
    if (!newStudent.school || !newStudent.school.trim()) {
      errors.school = "School is required";
    }
    if (!newStudent.programme || !newStudent.programme.trim()) {
      errors.programme = "Programme is required";
    }
    if (newStudent.credits === undefined || newStudent.credits === "" || isNaN(Number(newStudent.credits))) {
      errors.credits = "Valid credits number required";
    }
    if (!newStudent.cgpa || !String(newStudent.cgpa).trim()) {
      errors.cgpa = "CGPA is required";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    onSubmit(e);
  };

  const handleFieldChange = (field, value) => {
    setNewStudent({ ...newStudent, [field]: value });
    if (fieldErrors[field]) {
      setFieldErrors({ ...fieldErrors, [field]: null });
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "24px",
        maxWidth: "800px",
        margin: "0 auto",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* Top Header & Mode Toggle */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          borderBottom: "1px solid #f1f5f9",
          paddingBottom: "14px",
        }}
      >
        <div style={{ fontSize: "16px", fontWeight: 600, color: "#0f172a" }}>
          {activeMode === "single" ? "Register Graduate" : "Bulk Import Graduates"}
        </div>

        {/* Mode Switcher */}
        <div
          style={{
            display: "flex",
            background: "#f1f5f9",
            padding: "3px",
            borderRadius: "8px",
            gap: "4px",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setActiveMode("single");
              setImportResult(null);
            }}
            style={{
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: 500,
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
              backgroundColor: activeMode === "single" ? "#fff" : "transparent",
              color: activeMode === "single" ? "#ea580c" : "#64748b",
              boxShadow:
                activeMode === "single" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.15s ease",
            }}
          >
            Single Entry
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveMode("import");
              setImportResult(null);
            }}
            style={{
              padding: "6px 14px",
              fontSize: "12.5px",
              fontWeight: 500,
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
              backgroundColor: activeMode === "import" ? "#fff" : "transparent",
              color: activeMode === "import" ? "#ea580c" : "#64748b",
              boxShadow:
                activeMode === "import" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
              transition: "all 0.15s ease",
            }}
          >
            Import Excel / CSV
          </button>
        </div>
      </div>

      {/* MODE 1: SINGLE REGISTRATION FORM */}
      {activeMode === "single" && (
        <form noValidate onSubmit={handleSingleFormSubmit}>
          {Object.keys(fieldErrors).length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
                color: "#991b1b",
                fontSize: "12.5px",
                marginBottom: "16px",
              }}
            >
              <FiAlertCircle size={16} color="#dc2626" />
              <span>Please fill in all required fields highlighted in red below.</span>
            </div>
          )}

          <div
            className="admin-form-two-col"
            style={{
              gap: "14px 16px",
              marginBottom: "20px",
            }}
          >
            {/* Registration Number */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.reg_no ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Registration Number
              </label>
              <input
                type="text"
                placeholder="Registration Number"
                value={newStudent.reg_no || ""}
                onChange={(e) => handleFieldChange("reg_no", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.reg_no
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.reg_no ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.reg_no && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.reg_no}
                </div>
              )}
            </div>

            {/* Student Full Name */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.student_name ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Full Name
              </label>
              <input
                type="text"
                placeholder="Full Name"
                value={newStudent.student_name || ""}
                onChange={(e) => handleFieldChange("student_name", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.student_name
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.student_name ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.student_name && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.student_name}
                </div>
              )}
            </div>

            {/* Email Address */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.email ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Email Address
              </label>
              <input
                type="email"
                placeholder="student@example.com"
                value={newStudent.email || ""}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.email
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.email ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.email && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.email}
                </div>
              )}
            </div>

            {/* Gender */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: "#475569",
                  marginBottom: "5px",
                }}
              >
                Gender
              </label>
              <select
                value={newStudent.gender || "M"}
                onChange={(e) => handleFieldChange("gender", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 10px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                  color: "#334155",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              >
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Graduating Batch */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.batch ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Batch
              </label>
              <input
                type="text"
                placeholder="Batch"
                value={newStudent.batch || ""}
                onChange={(e) => handleFieldChange("batch", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.batch
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.batch ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.batch && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.batch}
                </div>
              )}
            </div>

            {/* Faculty */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.faculty ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Faculty
              </label>
              <input
                type="text"
                placeholder="Faculty"
                value={newStudent.faculty || ""}
                onChange={(e) => handleFieldChange("faculty", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.faculty
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.faculty ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.faculty && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.faculty}
                </div>
              )}
            </div>

            {/* School */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.school ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                School
              </label>
              <input
                type="text"
                placeholder="School"
                value={newStudent.school || ""}
                onChange={(e) => handleFieldChange("school", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.school
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.school ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.school && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.school}
                </div>
              )}
            </div>

            {/* Programme */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.programme ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Programme
              </label>
              <input
                type="text"
                placeholder="Programme"
                value={newStudent.programme || ""}
                onChange={(e) => handleFieldChange("programme", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.programme
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.programme ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.programme && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.programme}
                </div>
              )}
            </div>

            {/* Specialization */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: "#475569",
                  marginBottom: "5px",
                }}
              >
                Specialization
              </label>
              <input
                type="text"
                placeholder="Specialization (optional)"
                value={newStudent.specialization || ""}
                onChange={(e) =>
                  handleFieldChange("specialization", e.target.value)
                }
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Total Credits */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.credits ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                Total Credits
              </label>
              <input
                type="number"
                placeholder="Credits"
                value={newStudent.credits ?? ""}
                onChange={(e) => handleFieldChange("credits", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.credits
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.credits ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.credits && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.credits}
                </div>
              )}
            </div>

            {/* CGPA */}
            <div style={{ gridColumn: "span 2" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: fieldErrors.cgpa ? "#dc2626" : "#475569",
                  marginBottom: "5px",
                }}
              >
                CGPA
              </label>
              <input
                type="text"
                placeholder="CGPA"
                value={newStudent.cgpa || ""}
                onChange={(e) => handleFieldChange("cgpa", e.target.value)}
                style={{
                  width: "100%",
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: fieldErrors.cgpa
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  fontSize: "13px",
                  backgroundColor: fieldErrors.cgpa ? "#fff5f5" : "#f8fafc",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {fieldErrors.cgpa && (
                <div style={{ color: "#dc2626", fontSize: "11px", marginTop: "3px" }}>
                  {fieldErrors.cgpa}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={formLoading}
            style={{
              width: "100%",
              height: "40px",
              backgroundColor: "#ea580c",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              borderRadius: "6px",
              border: "none",
              cursor: formLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.15s ease",
            }}
          >
            {formLoading ? "Registering..." : "Register Graduate"}
          </button>
        </form>
      )}

      {/* MODE 2: BULK IMPORT EXCEL / CSV */}
      {activeMode === "import" && (
        <div>
          {/* Result Alert if any */}
          {importResult && (
            <div
              style={{
                padding: "14px 16px",
                borderRadius: "8px",
                marginBottom: "18px",
                backgroundColor: importResult.success ? "#f0fdf4" : "#fef2f2",
                border: importResult.success
                  ? "1px solid #86efac"
                  : "1px solid #fca5a5",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: importResult.success ? "#166534" : "#991b1b",
                  fontSize: "13.5px",
                  fontWeight: 500,
                  marginBottom: importResult.data ? "10px" : "0",
                }}
              >
                {importResult.success ? (
                  <FiCheckCircle size={18} color="#16a34a" />
                ) : (
                  <FiAlertCircle size={18} color="#dc2626" />
                )}
                <span>{importResult.message}</span>
              </div>

              {importResult.data && (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: 600,
                      backgroundColor: "#dcfce7",
                      color: "#15803d",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    ✓ {importResult.data.inserted || 0} Added
                  </span>

                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: 600,
                      backgroundColor: "#fef3c7",
                      color: "#b45309",
                      border: "1px solid #fde68a",
                    }}
                  >
                    ⚠ {importResult.data.duplicates || 0} Duplicates Skipped
                  </span>

                  {importResult.data.invalid > 0 && (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: 600,
                        backgroundColor: "#fee2e2",
                        color: "#b91c1c",
                        border: "1px solid #fecaca",
                      }}
                    >
                      ✕ {importResult.data.invalid} Invalid Skipped
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Drag and Drop Zone */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            style={{
              border: `2px dashed ${dragActive ? "#ea580c" : "#cbd5e1"}`,
              borderRadius: "10px",
              padding: "36px 20px",
              textAlign: "center",
              backgroundColor: dragActive ? "#fff7ed" : "#f8fafc",
              cursor: "pointer",
              marginBottom: "16px",
              transition: "all 0.2s ease",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx, .xls, .csv"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <FiUploadCloud
              size={36}
              color={dragActive ? "#ea580c" : "#94a3b8"}
              style={{ marginBottom: "10px" }}
            />
            <div
              style={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#1e293b",
                marginBottom: "4px",
              }}
            >
              {importFile ? importFile.name : "Click to browse or drop file here"}
            </div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              {importFile
                ? `${(importFile.size / 1024).toFixed(1)} KB`
                : "Supports .xlsx, .xls, and .csv files"}
            </div>
          </div>

          {/* Action Row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {/* Download Template Button */}
            <button
              type="button"
              onClick={handleDownloadTemplate}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                fontSize: "12.5px",
                fontWeight: 500,
                color: "#475569",
                background: "#f1f5f9",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }}
            >
              <FiDownload size={14} /> Download Sample Template
            </button>

            {/* Upload Button */}
            <button
              type="button"
              disabled={!importFile || importLoading}
              onClick={handleImportSubmit}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 20px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#fff",
                backgroundColor: !importFile || importLoading ? "#fdba74" : "#ea580c",
                border: "none",
                borderRadius: "6px",
                cursor: !importFile || importLoading ? "not-allowed" : "pointer",
                transition: "background-color 0.15s ease",
              }}
            >
              <FiUploadCloud size={15} />
              {importLoading ? "Importing..." : "Upload & Import"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddStudentForm;

